import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import XYZ from 'ol/source/XYZ';
import VectorSource from 'ol/source/Vector';
import GeoJSON from 'ol/format/GeoJSON';
import { defaults as defaultControls, FullScreen, ScaleLine } from 'ol/control';
import Overlay from 'ol/Overlay';
import { getLayerData, getGeomById, getPickupInfo } from '../services/api';
import * as OlExtent from 'ol/extent';
import { Fill, Stroke, Style } from 'ol/style';
import { 
  landspolStyle, 
  zonespolStyle, 
  ooptStyle, 
  admpolStyle, 
  dtplinStyle, 
  dtppntStyle,
  createPickuppntStyle 
} from '../utils/layerStyles';
import { LAYER_ORDER } from '../utils/mapConfig';
import 'ol/ol.css';

/**
 * TODO:
 * - Добавить функционал экспорта карты в изображение (html2canvas)
 */

// Конфигурация стилей для слоев
const LAYER_STYLES = {
  zonespol: zonespolStyle,
  zninspol: zonespolStyle, // Использует тот же стиль что zonespol
  oopt: ooptStyle,
  admpol: admpolStyle,
  dtplin: dtplinStyle,
  dtppnt: dtppntStyle,
  pickuppnt: createPickuppntStyle({})
};

const GISMap = forwardRef(function GISMap({ apiUrl, defaultCenter, defaultZoom, filters, selectedArea, onMapReady }, ref) {
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const popupRef = useRef(null);
  const overlayRef = useRef(null);
  const [map, setMap] = useState(null);
  const layersRef = useRef({});

  // Загрузка слоя
  const loadLayer = async (layerId, visible = true) => {
    if (!map) return false;

    try {
      // Если слой уже загружен, просто обновляем видимость
      if (layersRef.current[`${layerId}Layer`]) {
        layersRef.current[`${layerId}Layer`].setVisible(visible);
        return true;
      }

      // Загружаем данные слоя
      const geoJsonData = await getLayerData(layerId, '', {}, apiUrl);
      
      if (!geoJsonData || !geoJsonData.features || geoJsonData.features.length === 0) {
        console.warn(`No ${layerId} features loaded`);
        return false;
      }

      // Добавляем source к каждой feature
      geoJsonData.features.forEach(f => {
        if (f.properties) {
          f.properties.source = layerId;
        }
      });

      // Создаем формат GeoJSON
      const geoJsonFormat = new GeoJSON();
      const features = geoJsonFormat.readFeatures(geoJsonData, {
        featureProjection: 'EPSG:3857',
        dataProjection: 'EPSG:3857'
      });

      // Создаем source
      const source = new VectorSource({
        features: features
      });

      // Создаем слой
      const styleFunction = LAYER_STYLES[layerId];
      const layer = new VectorLayer({
        source: source,
        style: styleFunction || landspolStyle,
        visible: visible
      });
      layer.id = layerId;
      layer.setZIndex(LAYER_ORDER[layerId] || 40);

      // Сохраняем ссылки
      layersRef.current[`${layerId}Source`] = source;
      layersRef.current[`${layerId}Layer`] = layer;

      // Добавляем слой на карту
      map.addLayer(layer);

      return true;
    } catch (error) {
      console.error(`Error loading layer ${layerId}:`, error);
      return false;
    }
  };

  // Удаление слоя
  const removeLayer = (layerId) => {
    if (!map) return;

    const layer = layersRef.current[`${layerId}Layer`];
    if (layer) {
      map.removeLayer(layer);
      const source = layersRef.current[`${layerId}Source`];
      if (source) {
        source.clear();
      }
      delete layersRef.current[`${layerId}Layer`];
      delete layersRef.current[`${layerId}Source`];
    }
  };

  // Переключение видимости слоя
  const toggleLayerVisibility = (layerId, visible) => {
    const layer = layersRef.current[`${layerId}Layer`];
    if (layer) {
      layer.setVisible(visible);
      return true;
    }
    return false;
  };

  // Создание или получение слоя выделения
  const getSelectLayer = () => {
    if (!layersRef.current.selectLayer) {
      const selectSource = new VectorSource();
      const selectStyle = (feature, resolution) => {
        return new Style({
          stroke: new Stroke({
            color: [10, 200, 150, 1],
            width: 3
          }),
          fill: new Fill({
            color: [10, 125, 100, 0.1]
          })
        });
      };
      
      layersRef.current.selectLayer = new VectorLayer({
        source: selectSource,
        style: selectStyle,
        zIndex: LAYER_ORDER.select || 1000
      });
      layersRef.current.selectLayer.id = 'select';
      layersRef.current.selectSource = selectSource;
      
      if (map) {
        map.addLayer(layersRef.current.selectLayer);
      }
    }
    return layersRef.current.selectLayer;
  };

  // Выделение объектов на карте
  const selectFeatures = async (tableName, id) => {
    if (!map) return false;

    try {
      const geoJsonData = await getGeomById(tableName, id, apiUrl);
      
      if (!geoJsonData || !geoJsonData.features || geoJsonData.features.length === 0) {
        console.warn(`No features found for ${tableName}/${id}`);
        return false;
      }

      const selectLayer = getSelectLayer();
      const selectSource = layersRef.current.selectSource;
      selectSource.clear();

      const geoJsonFormat = new GeoJSON();
      const features = geoJsonFormat.readFeatures(geoJsonData, {
        featureProjection: 'EPSG:3857',
        dataProjection: 'EPSG:3857'
      });

      selectSource.addFeatures(features);
      map.render();
      return true;
    } catch (error) {
      console.error(`Error selecting features for ${tableName}/${id}:`, error);
      return false;
    }
  };

  // Переход к объектам на карте
  const gotoFeatures = async (tableName, id) => {
    if (!map) return false;

    try {
      const geoJsonData = await getGeomById(tableName, id, apiUrl);
      
      if (!geoJsonData || !geoJsonData.features || geoJsonData.features.length === 0) {
        console.warn(`No features found for ${tableName}/${id}`);
        return false;
      }

      const geoJsonFormat = new GeoJSON();
      const features = geoJsonFormat.readFeatures(geoJsonData, {
        featureProjection: 'EPSG:3857',
        dataProjection: 'EPSG:3857'
      });

      // Собираем координаты всех объектов
      let coords = [];
      features.forEach(f => {
        if (f.getGeometry()) {
          const geometry = f.getGeometry();
          const geometryCoords = geometry.getCoordinates();
          coords.push(geometryCoords);
        }
      });

      if (coords.length === 0) return false;

      // Преобразуем вложенные массивы координат в плоский массив
      while (coords.length > 0 && !(coords[0].length === 2 && !isNaN(coords[0][0]))) {
        coords = coords.reduce((a, b) => {
          return Array.isArray(b[0]) ? [...a, ...b] : [...a, b];
        }, []);
      }

      if (coords.length === 0) return false;

      const view = map.getView();
      const extent = OlExtent.boundingExtent(coords);
      
      if (!extent) return false;

      const center = OlExtent.getCenter(extent);
      view.setCenter(center);
      
      const resolution = view.getResolutionForExtent(extent);
      if (resolution) {
        view.setResolution(resolution);
      }

      // Также выделяем объекты
      await selectFeatures(tableName, id);
      
      return true;
    } catch (error) {
      console.error(`Error going to features for ${tableName}/${id}:`, error);
      return false;
    }
  };

  // Очистка выделения
  const clearSelection = () => {
    if (layersRef.current.selectSource) {
      layersRef.current.selectSource.clear();
      if (map) {
        map.render();
      }
    }
  };

  // Экспорт методов управления слоями через ref
  useImperativeHandle(ref, () => ({
    loadLayer,
    removeLayer,
    toggleLayerVisibility,
    selectFeatures,
    gotoFeatures,
    clearSelection,
    getMap: () => map
  }));

  useEffect(() => {
    if (!mapContainerRef.current) return;

    // Создаем стандартные контролы навигации OpenLayers
    // defaultControls() уже включает: Zoom (кнопки увеличения/уменьшения), Attribution (атрибуция), Rotate (кнопка поворота)
    const controls = defaultControls().extend([
      new FullScreen(),        // Полноэкранный режим
      new ScaleLine({          // Масштабная линейка
        units: 'metric',       // Метрические единицы
        bar: true,             // Отображать полосу
        steps: 1,              // Количество шагов
        text: true             // Отображать текст
      })
    ]);

    const olMap = new Map({
      target: mapContainerRef.current,
      controls: controls,
      view: new View({
        projection: 'EPSG:3857',
        center: defaultCenter,
        zoom: defaultZoom
      }),
      layers: [
        new TileLayer({
          source: new XYZ({
            url: `${apiUrl}/tiles/{z}/{x}/{y}.png`
          })
        })
      ]
    });

    setMap(olMap);
    mapRef.current = olMap;
    if (onMapReady) {
      onMapReady(olMap);
    }

    return () => {
      if (olMap) {
        olMap.setTarget(null);
      }
    };
  }, []);

  // Создание слоя выделения при инициализации карты
  useEffect(() => {
    if (!map) return;
    getSelectLayer();
  }, [map]);

  // Загрузка постоянного слоя landspol
  useEffect(() => {
    if (!map) return;

    const loadLandspolLayer = async () => {
      try {
        const geoJsonData = await getLayerData('landspol', '', {}, apiUrl);
        
        if (!geoJsonData || !geoJsonData.features || geoJsonData.features.length === 0) {
          console.warn('No landspol features loaded');
          return;
        }

        geoJsonData.features.forEach(f => {
          if (f.properties) {
            f.properties.source = 'landspol';
          }
        });

        const geoJsonFormat = new GeoJSON();
        const features = geoJsonFormat.readFeatures(geoJsonData, {
          featureProjection: 'EPSG:3857',
          dataProjection: 'EPSG:3857'
        });

        if (!layersRef.current.landspolSource) {
          layersRef.current.landspolSource = new VectorSource({
            features: features
          });
        } else {
          layersRef.current.landspolSource.clear();
          layersRef.current.landspolSource.addFeatures(features);
        }

        if (!layersRef.current.landspolLayer) {
          layersRef.current.landspolLayer = new VectorLayer({
            source: layersRef.current.landspolSource,
            style: landspolStyle,
            visible: true
          });
          layersRef.current.landspolLayer.id = 'landspol';
          layersRef.current.landspolLayer.setZIndex(LAYER_ORDER.landspol || 10);
          
          map.addLayer(layersRef.current.landspolLayer);
        }
      } catch (error) {
        console.error('Error loading landspol layer:', error);
      }
    };

    loadLandspolLayer();
  }, [map, apiUrl]);

  // Загрузка постоянного слоя pickuppnt
  useEffect(() => {
    if (!map) return;

    const loadPickuppntLayer = async () => {
      try {
        const geoJsonData = await getLayerData('pickuppnt', 'nodate', {}, apiUrl);
        if (!geoJsonData?.features?.length) {
          console.warn('No pickuppnt features loaded');
          return;
        }
        geoJsonData.features.forEach(f => {
          if (f.properties) f.properties.source = 'pickuppnt';
        });
        const geoJsonFormat = new GeoJSON();
        const features = geoJsonFormat.readFeatures(geoJsonData, {
          featureProjection: 'EPSG:3857',
          dataProjection: 'EPSG:3857'
        });
        if (!layersRef.current.pickuppntSource) {
          layersRef.current.pickuppntSource = new VectorSource({ features });
        } else {
          layersRef.current.pickuppntSource.clear();
          layersRef.current.pickuppntSource.addFeatures(features);
        }
        if (!layersRef.current.pickuppntLayer) {
          layersRef.current.pickuppntLayer = new VectorLayer({
            source: layersRef.current.pickuppntSource,
            style: createPickuppntStyle({}),
            visible: true
          });
          layersRef.current.pickuppntLayer.id = 'pickuppnt';
          layersRef.current.pickuppntLayer.setZIndex(LAYER_ORDER.pickuppnt || 15);
          map.addLayer(layersRef.current.pickuppntLayer);
        }
      } catch (error) {
        console.error('Error loading pickuppnt layer:', error);
      }
    };

    loadPickuppntLayer();
  }, [map, apiUrl]);

  // Обновление стиля pickuppnt при смене фильтров (подсветка попадающих под фильтр)
  useEffect(() => {
    const layer = layersRef.current?.pickuppntLayer;
    if (layer) {
      layer.setStyle(createPickuppntStyle(filters || {}));
    }
  }, [filters]);

  // Overlay и обработка клика по пункту выдачи
  useEffect(() => {
    if (!map || !popupRef.current) return;

    if (!overlayRef.current) {
      overlayRef.current = new Overlay({
        element: popupRef.current,
        positioning: 'bottom-center',
        stopEvent: false,
        offset: [0, -10]
      });
      map.addOverlay(overlayRef.current);
    }

    const overlay = overlayRef.current;

    const handleClick = async (evt) => {
      const feature = map.forEachFeatureAtPixel(evt.pixel, (f) => f);
      if (feature) {
        const source = feature.get('source') ?? feature.getProperties()?.source;
        if (source === 'pickuppnt') {
          const id = feature.get('gid') ?? feature.getProperties()?.gid;
          if (id != null) {
            popupRef.current.innerHTML = '<p>Загрузка...</p>';
            overlay.setPosition(evt.coordinate);
            try {
              const data = await getPickupInfo(id, apiUrl);
              if (data) {
                const parts = [];
                if (data.huntuser_name) parts.push(`<div><strong>Охотпользователь:</strong> ${escapeHtml(data.huntuser_name)}</div>`);
                if (data.economy_name) parts.push(`<div><strong>Охотхозяйство:</strong> ${escapeHtml(data.economy_name)}</div>`);
                if (data.region_name) parts.push(`<div><strong>Район:</strong> ${escapeHtml(data.region_name)}</div>`);
                if (data.pickup_address) parts.push(`<div><strong>Адрес:</strong> ${escapeHtml(data.pickup_address)}</div>`);
                if (data.pickup_desc) parts.push(`<div><strong>Описание:</strong> ${escapeHtml(data.pickup_desc)}</div>`);
                popupRef.current.innerHTML = parts.length ? parts.join('') : '<p>Нет данных</p>';
              } else {
                popupRef.current.innerHTML = '<p>Нет данных</p>';
              }
            } catch (err) {
              popupRef.current.innerHTML = '<p class="ol-popup-error">Ошибка загрузки</p>';
            }
            return;
          }
        }
      }
      overlay.setPosition(undefined);
    };

    function escapeHtml(str) {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }

    map.on('click', handleClick);
    return () => map.un('click', handleClick);
  }, [map, apiUrl]);

  return (
    <div 
      ref={mapContainerRef} 
      className="gis-map"
      style={{ width: '100%', height: '100%', position: 'relative' }}
    >
      <div ref={popupRef} className="ol-popup" />
    </div>
  );
});

export default GISMap;
